import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS, DetailsCard } from "components";
import { CHECKLIST_TYPES } from "models";
import { ChecklistPDF } from "modules/AppointmentDetails/components";
import "modules/AppointmentDetails/components/Checks/components/Check/Check.scss";
import { useConnectedInterventionIdToFilter, useConnectedQuestionResultIdToFilter } from "modules/AppointmentDetails/hooks";
import { QuestionResults } from "modules/HoldingAppointmentDetails/components/Checks/components/Check/components/QuestionResults";
import { formatDate, subtractSecondsAndFormatDate } from "utils/dateHelperFunctions";

import type { Appointment } from "models";
import type { OrderedGroupedCheck } from "modules/AppointmentDetails/hooks";

type CheckProps = {
  isExpanded: boolean;
  check: OrderedGroupedCheck;
  appointment: Appointment;
  sectionMenuId: string;
  icon?: string;
};

export const Check = ({ sectionMenuId, isExpanded, check, appointment, icon }: CheckProps) => {
  const { t } = useTranslation();

  const [showAll, setShowAll] = useState(false);

  const connectedInterventionId = useConnectedInterventionIdToFilter();
  const connectedQuestionResultId = useConnectedQuestionResultIdToFilter();

  const checkIsNotConnected = connectedInterventionId && connectedInterventionId !== check.intervention_id;
  const checkQuestionResultIsNotConnected = connectedQuestionResultId && !check.question_items.some(e => e.id === connectedQuestionResultId);

  if (checkIsNotConnected || checkQuestionResultIsNotConnected) return null;

  const isExtraPart = check.checklist?.checklist_type === CHECKLIST_TYPES.ExtraParts;
  const name = isExtraPart ? t("v8_extra_parts", "Extra parts") : check.checklist?.name;

  const title = (
    <div className="Check-title">
      {check.purpose ? (
        <Popup
          basic
          wide="very"
          className="Check-title-popup"
          content={<span>{check.purpose}</span>}
          trigger={<span className="ellipsis">{`#${check._order} ${name} - ${check.purpose}`} </span>}
        />
      ) : (
        <span className="ellipsis">{`#${check._order} ${name}`}</span>
      )}

      {!check.checklist?.template?.hide_timestamp && check.mechanic && (
        <span className="mechanic-info">
          <span>{` - ${check.mechanic.first_name} ${check.mechanic.last_name}`}</span>
          <span className="separator">-</span>
          <span className="first-date">{`${subtractSecondsAndFormatDate(check.created_on, check.duration, DATE_FORMATS_FNS.timeDateMonthYearShort)}`}</span>
          <Icon className="arrow right" />
          <span className="second-date">{`${formatDate(check.created_on, DATE_FORMATS_FNS.timeDateMonthYearShort)}`}</span>
        </span>
      )}
    </div>
  );

  const toggleShowAll = () => setShowAll(prev => !prev);

  return (
    <DetailsCard
      sectionMenuId={sectionMenuId}
      title={title}
      icon={check._isTyreCheck ? "tire" : isExtraPart ? "briefcase" : icon}
      isExpanded={isExpanded}
      isChecklistTitle={true}
      backgroundColor={check.checklist?.template?.primary_color || ""}
      borderColor={check.checklist?.template?.text_color || ""}
      textColor={check.checklist?.template?.text_color || ""}
      rightContent={
        <div className="full end auto-width">
          {check.checklist?.template?.total_pdf_enabled && (
            <ChecklistPDF appointment={appointment} checkId={check.id} isPreviewable={check.checklist.template.is_previewable} />
          )}

          <Button className="-appointment-status icon" size="small" onClick={toggleShowAll}>
            <Icon className="bars" color={showAll ? "green" : "grey"} />
          </Button>
        </div>
      }
    >
      <QuestionResults check={check} showAll={showAll} appointment={appointment} />
    </DetailsCard>
  );
};
